/**
 * Transition policy for moving between playlist items.
 *
 * Convention: the transition stored on a PlaylistItem is the transition
 * INTO that item ("incoming wins"). Durations are inclusive — they straddle
 * the slot boundary, half on each side, so the scheduling layer's
 * wall-clock math is preserved.
 *
 * Video involvement: crossfade between items where either side renders a
 * video media binding falls back to fade-through-black, because two
 * simultaneous 1080p H.264 decoders may exceed Pi-class device capacity.
 */

import type { PlaylistItem, LayoutZone } from '../lib/types';

export type TransitionKind = 'cut' | 'fade' | 'crossfade';

/** Total transition window length in ms. 0 for cut. */
export const TRANSITION_DURATION_MS: Record<TransitionKind, number> = {
  cut: 0,
  fade: 500,
  crossfade: 500,
};

export function normalizeTransition(value: string | undefined | null): TransitionKind {
  if (value === 'fade' || value === 'crossfade') return value;
  return 'cut';
}

/**
 * Effective transition for moving from `from` to `to`. May rewrite
 * crossfade → fade if either side renders a video media binding, to avoid
 * dual-decoder load on Pi-class devices.
 */
export function effectiveTransition(
  from: PlaylistItem | null,
  to: PlaylistItem | null,
): TransitionKind {
  const requested = normalizeTransition(to?.transition);
  if (requested !== 'crossfade') return requested;
  if (itemRendersVideo(from) || itemRendersVideo(to)) return 'fade';
  return 'crossfade';
}

function itemRendersVideo(item: PlaylistItem | null): boolean {
  if (!item) return false;
  for (const binding of Object.values(item.zones)) {
    if (!binding || typeof binding !== 'object') continue;
    const b = binding as { kind?: string; mimeType?: string };
    if (b.kind === 'media' && typeof b.mimeType === 'string' && b.mimeType.startsWith('video/')) {
      return true;
    }
  }
  return false;
}

/**
 * Zone classification.
 *   - transient: belongs to an item; swaps when items change. Participates
 *     in transitions.
 *   - continuous: persists across item changes (ticker text, clock face,
 *     etc.) and does NOT participate in transitions.
 *
 * Drives the two-layer rendering: only transient zones are duplicated
 * during the transition window.
 */
export function isTransientZone(zone: Pick<LayoutZone, 'zoneType'>): boolean {
  switch (zone.zoneType) {
    case 'media':
    case 'html5_embed':
      return true;
    // Continuous zones, including the programme zones: they render data rather
    // than playlist content, so they must not blink on every item change.
    case 'ticker':
    case 'clock':
    case 'live_feed':
    case 'performance_detail':
    case 'performance_schedule':
    case 'sponsor_strip':
      return false;
    default:
      // Unknown zone types render as transient by default (safer — they may
      // be media-like).
      return true;
  }
}
